import { ApiBlobError, fetchApiBlob } from './api';

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
  });
}

/**
 * One in-flight thumbnail per tab, at most two starts per second. ABC's
 * shared product budget also serves search/details and other users. Keep
 * room for those requests and honor server cooldowns across the whole queue.
 * Only queued work is retained here, never image bytes or credentials.
 */
export function createCatalogImageLoader(
  load: (endpoint: string, signal?: AbortSignal) => Promise<Blob>,
) {
  let tail: Promise<unknown> = Promise.resolve();
  let nextStart = 0;
  let cooldownUntil = 0;

  return (endpoint: string, signal?: AbortSignal): Promise<Blob> => {
    const result = tail.then(async () => {
      for (let attempt = 0; ; attempt++) {
        signal?.throwIfAborted();
        const delay = Math.max(nextStart, cooldownUntil) - Date.now();
        if (delay > 0) await wait(delay, signal);
        signal?.throwIfAborted();
        nextStart = Date.now() + 500;
        try {
          return await load(endpoint, signal);
        } catch (error) {
          if (!(error instanceof ApiBlobError) || error.status !== 429) throw error;
          const pause = Math.max(1000 * 2 ** attempt, error.retryAfter ?? 0);
          cooldownUntil = Math.max(cooldownUntil, Date.now() + pause);
          // Bounded retries for read-only images. A longer provider pause is
          // respected by subsequent work, never shortened to force a retry.
          if (attempt >= 2 || pause > 30_000) throw error;
        }
      }
    });
    // A missing image or canceled search must not poison later thumbnails.
    tail = result.then(() => undefined, () => undefined);
    return result;
  };
}

export const loadCatalogImage = createCatalogImageLoader(
  (endpoint, signal) => fetchApiBlob(endpoint, { signal }),
);
